import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="py-8 px-6 border-t border-[var(--bg-elevated)]" data-id="element-146">
        <div className="max-w-[680px] mx-auto flex justify-center items-center gap-6" data-id="element-147">
          <button onClick={() => setActiveModal('privacy')} className="text-sm text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors cursor-pointer" data-id="element-148">
            Política de Privacidad
          </button>
          <span className="text-[var(--bg-elevated)]" data-id="element-149">|</span>
          <button onClick={() => setActiveModal('terms')} className="text-sm text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors cursor-pointer" data-id="element-150">
            Términos y Condiciones
          </button>
        </div>
        <p className="text-center text-xs text-[var(--text-muted)] opacity-50 mt-4" data-id="element-151">
          © {new Date().getFullYear()} Apaga La Mente. Todos los derechos
          reservados.
        </p>
      </footer>

      <AnimatePresence data-id="element-152">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-153">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} onClick={e => e.stopPropagation()} className="bg-[var(--bg-surface)] border border-[var(--bg-elevated)] rounded-sm w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" data-id="element-154">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--bg-elevated)]" data-id="element-155">
                <h3 className="text-lg font-serif text-[var(--text-heading)]" data-id="element-156">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors p-1" aria-label="Cerrar" data-id="element-157">
                  <X className="w-5 h-5" data-id="element-158" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(80vh-64px)] text-[var(--text-primary)] text-sm leading-relaxed space-y-4" data-id="element-159">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-160" /> : <TermsContent data-id="element-161" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-[var(--text-muted)] text-xs uppercase tracking-widest mb-4" data-id="element-162">
        Última actualización: Febrero 2026
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-163">
        1. Información que recopilamos
      </h4>
      <p data-id="element-164">
        Al adquirir "Apaga La Mente", recopilamos únicamente la información
        necesaria para procesar tu compra: dirección de correo electrónico y
        datos de pago. No almacenamos datos de tarjetas de crédito directamente;
        estos son procesados de forma segura por nuestro proveedor de pagos.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-165">
        2. Uso de la información
      </h4>
      <p data-id="element-166">Tu información se utiliza exclusivamente para:</p>
      <ul className="list-disc pl-5 space-y-1 text-[var(--text-muted)]" data-id="element-167">
        <li data-id="element-168">Entregar el producto digital (método) a tu correo electrónico</li>
        <li data-id="element-169">Enviar confirmación de compra</li>
        <li data-id="element-170">Brindarte soporte si lo necesitas</li>
      </ul>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-171">
        3. Compartir información
      </h4>
      <p data-id="element-172">
        No vendemos, alquilamos ni compartimos tu información personal con
        terceros, excepto los proveedores de servicios necesarios para procesar
        tu compra y entrega del producto.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-173">
        4. Cookies
      </h4>
      <p data-id="element-174">
        Este sitio puede utilizar cookies esenciales para el funcionamiento
        básico de la página. No utilizamos cookies de seguimiento publicitario.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-175">
        5. Seguridad
      </h4>
      <p data-id="element-176">
        Implementamos medidas de seguridad razonables para proteger tu
        información personal. Sin embargo, ninguna transmisión por Internet es
        100% segura.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-177">
        6. Tus derechos
      </h4>
      <p data-id="element-178">
        Puedes solicitar la eliminación de tus datos personales en cualquier
        momento. Para ejercer este derecho, contáctanos y procesaremos tu
        solicitud en un plazo razonable.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-[var(--text-muted)] text-xs uppercase tracking-widest mb-4" data-id="element-179">
        Última actualización: Febrero 2026
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-180">
        1. Producto
      </h4>
      <p data-id="element-181">
        "Apaga La Mente: Protocolo de 14 Noches" es un producto digital en
        formato de método descargable. Al completar la compra, recibirás acceso
        inmediato al archivo descargable.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-182">
        2. Uso personal
      </h4>
      <p data-id="element-183">
        El contenido del método es para uso personal y privado. No está
        permitido redistribuir, copiar, revender o compartir el material sin
        autorización expresa por escrito.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-184">
        3. Naturaleza del contenido
      </h4>
      <p data-id="element-185">
        Este producto es de carácter educativo e informativo. No constituye
        consejo médico, psicológico ni terapéutico. Si experimentas problemas
        severos de sueño, ansiedad clínica u otras condiciones de salud,
        consulta a un profesional de la salud.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-186">
        4. Resultados
      </h4>
      <p data-id="element-187">
        Los resultados pueden variar de persona a persona. No garantizamos
        resultados específicos, ya que dependen de factores individuales y de la
        aplicación consistente del protocolo.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-188">
        5. Política de reembolso
      </h4>
      <p data-id="element-189">
        Debido a la naturaleza digital del producto, todas las ventas son
        finales. Si tienes algún problema con tu compra o acceso al archivo,
        contáctanos y haremos lo posible por ayudarte.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-190">
        6. Propiedad intelectual
      </h4>
      <p data-id="element-191">
        Todo el contenido, incluyendo textos, diseño y metodología, está
        protegido por derechos de autor. El uso no autorizado puede resultar en
        acciones legales.
      </p>

      <h4 className="text-base font-semibold text-[var(--text-heading)] mt-6 mb-2" data-id="element-192">
        7. Modificaciones
      </h4>
      <p data-id="element-193">
        Nos reservamos el derecho de modificar estos términos en cualquier
        momento. Los cambios serán efectivos al momento de su publicación en
        esta página.
      </p>
    </>;
}